import { z } from 'zod';
import { CASE_PRIORITIES, CASE_SEVERITIES, CASE_STATUSES } from '../enums.js';
import { caseAttributeNameSchema } from './case-attribute.js';
import { environmentNameSchema } from './environment.js';
import { labelNameSchema, MAX_LABELS_PER_CASE } from './label.js';
import { nonEmptyString, optionalText, paginationSchema, uuidSchema } from './common.js';
export const MAX_STEPS_PER_CASE = 100;
/**
 * Free-text fields of a test case carry light Markdown (headings, lists, code
 * fences and pasted screenshots) rather than plain text — see
 * docs/FEATURES.md § Rich text.
 */
export const testCaseStepSchema = z.object({
    action: nonEmptyString(2000, 'Action'),
    expectedResult: optionalText(2000),
    /** Optional screenshot shown as a thumbnail next to the step. */
    screenshotId: uuidSchema.nullable().optional(),
});
const caseBody = {
    title: nonEmptyString(300, 'Title'),
    description: optionalText(10_000),
    preconditions: optionalText(5000),
    testData: optionalText(5000),
    priority: z.enum(CASE_PRIORITIES).default('medium'),
    severity: z.enum(CASE_SEVERITIES).default('minor'),
    status: z.enum(CASE_STATUSES).default('active'),
    isAutomated: z.boolean().default(false),
    // A test case always lives in a folder; projects are created with one.
    folderId: uuidSchema.describe('Folder'),
    // One of the project's environments, or none: a case that is not tied to a
    // particular stand is the normal case. Its layer and module work the same
    // way — picked from the project's catalogue, or left unset.
    environmentId: uuidSchema.nullable().optional(),
    layerId: uuidSchema.nullable().optional(),
    moduleId: uuidSchema.nullable().optional(),
    labelIds: z.array(uuidSchema).max(MAX_LABELS_PER_CASE).default([]),
    steps: z.array(testCaseStepSchema).max(MAX_STEPS_PER_CASE).default([]),
};
export const createTestCaseSchema = z.object(caseBody);
export const updateTestCaseSchema = z
    .object({
    title: caseBody.title.optional(),
    description: caseBody.description,
    preconditions: caseBody.preconditions,
    testData: caseBody.testData,
    priority: z.enum(CASE_PRIORITIES).optional(),
    severity: z.enum(CASE_SEVERITIES).optional(),
    status: z.enum(CASE_STATUSES).optional(),
    isAutomated: z.boolean().optional(),
    folderId: uuidSchema.optional(),
    environmentId: uuidSchema.nullable().optional(),
    layerId: uuidSchema.nullable().optional(),
    moduleId: uuidSchema.nullable().optional(),
    /** When present, the whole label set is replaced. */
    labelIds: z.array(uuidSchema).max(MAX_LABELS_PER_CASE).optional(),
    /** When present, the whole step collection is replaced. */
    steps: z.array(testCaseStepSchema).max(MAX_STEPS_PER_CASE).optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
export const TEST_CASE_SORT_FIELDS = ['createdAt', 'updatedAt', 'title', 'seqNumber', 'priority'];
export const listTestCasesSchema = paginationSchema.extend({
    folderId: uuidSchema.optional(),
    includeSubfolders: z.coerce.boolean().default(false),
    priority: z.enum(CASE_PRIORITIES).optional(),
    severity: z.enum(CASE_SEVERITIES).optional(),
    status: z.enum(CASE_STATUSES).optional(),
    isAutomated: z.coerce.boolean().optional(),
    labelId: uuidSchema.optional(),
    q: z.string().trim().max(200).optional(),
    sort: z.enum(TEST_CASE_SORT_FIELDS).default('seqNumber'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const bulkMoveTestCasesSchema = z.object({
    caseIds: z.array(uuidSchema).min(1, 'Select at least one test case').max(200),
    folderId: uuidSchema,
});
/**
 * Import from another test management system. Rows arrive already read and
 * mapped by the interface — this is the domain check of what they produced.
 *
 * What identifies a case inside this system — its key, author and timestamps —
 * is assigned here, never read from the file. The classification the file does
 * carry is kept: priority, labels, environment, layer and module arrive as the
 * names the other tool wrote, and are matched against this project's own lists
 * by name, adding what is missing. Severity, status and automation have no
 * counterpart worth guessing, so they keep their defaults.
 */
export const MAX_IMPORT_CASES = 1000;
export const importedTestCaseSchema = z.object({
    title: nonEmptyString(300, 'Title'),
    description: optionalText(10_000),
    preconditions: optionalText(5000),
    testData: optionalText(5000),
    steps: z
        .array(z.object({
        action: nonEmptyString(2000, 'Action'),
        expectedResult: optionalText(2000),
    }))
        .max(MAX_STEPS_PER_CASE)
        .default([]),
    /** Priority as the file spells it; the interface maps it onto ours. */
    priority: z.enum(CASE_PRIORITIES).optional(),
    /** Names, not ids: the file knows nothing about this project's rows. */
    labels: z.array(labelNameSchema).max(MAX_LABELS_PER_CASE).default([]),
    environment: environmentNameSchema.optional(),
    layer: caseAttributeNameSchema.optional(),
    module: caseAttributeNameSchema.optional(),
    /** Where this case came from in the file, so a skipped row can be named. */
    sourceRow: z.number().int().positive().optional(),
    sourceKey: z.string().trim().max(100).optional(),
});
export const importTestCasesSchema = z.object({
    /** Chosen by the person importing: the file's own structure is not used. */
    folderId: uuidSchema.describe('Folder'),
    /**
     * Checked one by one rather than as a typed array: a single malformed case
     * is reported in the result instead of rejecting the whole file.
     */
    cases: z.array(z.unknown()).min(1, 'There is nothing to import').max(MAX_IMPORT_CASES),
});
//# sourceMappingURL=test-case.js.map